import { useState } from "react";
import { ChevronRight, Zap, X, Eye } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { sevColor } from "../../lib/format";
import { cn } from "../../lib/utils";
import api from "../../lib/api";
import { toast } from "sonner";

export function InsightCard({ item, onChange, rank }) {
  const [open, setOpen] = useState(false);
  const c = sevColor(item.severity);

  const act = async (kind) => {
    try {
      await api.post(`/insights/${item.id}/${kind}`);
      toast.success(kind === "dismiss" ? "Insight dismissed" : "Action created");
      onChange && onChange();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <>
      <div
        className={cn("group border rounded-lg p-4 bg-white hover:shadow-sm transition-[box-shadow] duration-200 border-l-2", c.border)}
        style={{ borderLeftColor: "var(--tw-border)" }}
        data-testid={`insight-${item.id}`}
      >
        <div className="flex items-start gap-3">
          {rank != null && <span className="kpi-num text-lg font-extrabold text-slate-300 w-6 shrink-0">{rank}</span>}
          <span className={cn("mt-1.5 w-2 h-2 rounded-full shrink-0", c.dot)} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide", c.chip)}>{item.severity}</span>
              <span className="text-[11px] text-slate-400">{item.location} · {item.metric}</span>
            </div>
            <h4 className="text-sm font-semibold text-slate-800 mt-1.5 leading-snug">{item.title}</h4>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.description}</p>
            <div className="flex items-center gap-3 mt-3">
              <button onClick={() => setOpen(true)} className="text-xs font-medium text-primary hover:underline flex items-center gap-1" data-testid={`insight-view-${item.id}`}>
                <Eye className="w-3.5 h-3.5" /> View Details
              </button>
              <button onClick={() => act("action")} className="text-xs font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid={`insight-action-${item.id}`}>
                <Zap className="w-3.5 h-3.5" /> Create Action
              </button>
              <button onClick={() => act("dismiss")} className="text-xs font-medium text-slate-400 hover:text-slate-700 flex items-center gap-1" data-testid={`insight-dismiss-${item.id}`}>
                <X className="w-3.5 h-3.5" /> Dismiss
              </button>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-slate-400 shrink-0">{item.confidence}%</span>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto scrollbar-thin" data-testid="insight-drawer">
          <SheetHeader>
            <SheetTitle className="font-heading pr-6 leading-snug">{item.title}</SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-5">
            <div className={cn("rounded-lg p-3 border", c.bg, c.border)}>
              <div className="flex items-center gap-2 text-xs">
                <span className={cn("px-2 py-0.5 rounded-full font-semibold uppercase", c.chip)}>{item.severity}</span>
                <span className="text-slate-500">{item.location}</span>
                <span className="ml-auto font-semibold text-slate-600">Confidence {item.confidence}%</span>
              </div>
              <p className="text-sm text-slate-700 mt-2">{item.description}</p>
            </div>
            <Detail label="Evidence">
              <ul className="list-disc list-inside space-y-1">
                {(item.evidence || []).map((e, i) => <li key={i} className="text-sm text-slate-600">{e}</li>)}
              </ul>
            </Detail>
            <Detail label="Possible Cause"><p className="text-sm text-slate-600">{item.cause}</p></Detail>
            <Detail label="Business Impact"><p className="text-sm text-slate-600">{item.impact}</p></Detail>
            <Detail label="Recommendation">
              <p className="text-sm text-slate-800 font-medium bg-secondary/5 border-l-2 border-secondary p-3 rounded">{item.recommendation}</p>
            </Detail>
            <div className="flex gap-2 pt-2">
              <button onClick={() => { act("action"); setOpen(false); }} className="flex-1 bg-primary text-white text-sm font-medium rounded-md py-2 hover:bg-primary/90" data-testid="drawer-create-action">Create Action</button>
              <button onClick={() => { act("dismiss"); setOpen(false); }} className="px-4 border border-slate-200 text-sm rounded-md hover:bg-slate-50">Dismiss</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5 flex items-center gap-1">
        <ChevronRight className="w-3 h-3" /> {label}
      </div>
      {children}
    </div>
  );
}
